import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, Transition } from '@headlessui/react';
import { GiftIcon, FilmIcon, CurrencyDollarIcon, LifebuoyIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../../utils/formatting';

// ==========================================================
// HELPER COMPONENTS
// ==========================================================

const StatusBadge = ({ status }) => {
  const colorClasses = {
    'Delivered': 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    'Processing Pickup': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${colorClasses[status] || 'bg-gray-100 text-gray-800'}`}>{status.replace('_', ' ')}</span>;
};

const StatCard = ({ icon, title, value, color, isLink = false, to = '#' }) => {
    const content = (
        <div className={`p-6 rounded-lg text-white bg-gradient-to-br ${color} shadow-lg transition-transform hover:-translate-y-1`}>
            <div className="flex justify-between items-center">
                <div className="p-3 bg-white/20 rounded-full">{icon}</div>
                {isLink && <ArrowTopRightOnSquareIcon className="w-6 h-6 text-white/50" />}
            </div>
            <p className="mt-4 text-sm text-white/80">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
        </div>
    );
    return isLink ? <a href={to} className="block">{content}</a> : <div className="block">{content}</div>;
}

const VideoModal = ({ videoUrl, isOpen, onClose }) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={onClose}>
      <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
        <div className="fixed inset-0 bg-black/80" />
      </Transition.Child>
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-black text-left align-middle shadow-xl transition-all">
              <div className="aspect-w-16 aspect-h-9">
                <iframe 
                  src={videoUrl} 
                  title="Appreciation Video" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
);


// ==========================================================
//  MAIN DONOR GIFTS PAGE
// ==========================================================
export default function DonorGiftsPage() {
  const { token } = useAuth();
  const [donorData, setDonorData] = useState({ givingHistory: [], totalGiven: 0, giftsMade: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchGifts = async () => {
        if (!token) {
            setLoading(false);
            setError('You must be logged in to view your gifts.');
            return;
        };
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/donor/gifts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Could not fetch your gift history.');
            const data = await response.json();
            setDonorData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    fetchGifts();
  }, [token]);

  const openVideoModal = (url) => {
    setActiveVideoUrl(url);
    setIsModalOpen(true);
  };
  const closeVideoModal = () => {
    setIsModalOpen(false);
    setActiveVideoUrl('');
  };

  if (loading) return <p className="p-8 text-center text-xl">Loading your giving history...</p>;
  if (error) return <p className="p-8 text-center text-xl text-red-500">{error}</p>;

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">My Gifts</h1>
      <p className="mt-1 text-gray-500 dark:text-gray-400">Thank you for your incredible generosity. Here's a summary of your impact.</p>
      
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={<CurrencyDollarIcon className="h-8 w-8 text-white"/>} title="Total Given" value={formatCurrency(donorData.totalGiven)} color="from-green-500 to-green-600" />
        <StatCard icon={<GiftIcon className="h-8 w-8 text-white"/>} title="Gifts Made" value={donorData.giftsMade} color="from-blue-500 to-blue-600" />
        <StatCard icon={<LifebuoyIcon className="h-8 w-8 text-white"/>} title="Contact Support" value="Get Help" color="from-slate-500 to-slate-600" isLink={true} to="/contact" />
      </div>

      <div className="mt-12 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <h2 className="text-xl font-bold p-6 text-gray-900 dark:text-white">Your Gifting History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Appreciation</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
              {donorData.givingHistory.length > 0 ? donorData.givingHistory.map(gift => (
                <tr key={gift.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{gift.recipient_first_name} {gift.recipient_last_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{gift.product_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(gift.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={gift.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {gift.appreciation_video_url ? (
                      <button onClick={() => openVideoModal(gift.appreciation_video_url)} className="text-orange-600 hover:text-orange-800 flex items-center">
                        <FilmIcon className="w-5 h-5 mr-1" /> Watch Video
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 italic">Pending</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                    <td colSpan="4" className="text-center p-8 text-gray-500">You haven't made any gifts yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <VideoModal videoUrl={activeVideoUrl} isOpen={isModalOpen} onClose={closeVideoModal} />
    </div>
  );
}